import { supabase } from './supabaseService';

export interface ChatMessage {
  id: string;
  groupId: string;
  senderId: string;
  senderName: string;
  message: string;
  messageType: 'text' | 'image' | 'file' | 'system';
  isAdmin: boolean;
  isRead: boolean;
  createdAt: string;
}

export class ChatService {
  /**
   * Get chat messages for a group
   */
  async getGroupMessages(groupId: string): Promise<ChatMessage[]> {
    try {
      const { data: messages, error } = await supabase
        .from('group_chat_messages')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch chat messages: ${error.message}`);
      }

      return messages.map((msg: any) => ({
        id: msg.id,
        groupId: msg.group_id,
        senderId: msg.sender_id,
        senderName: msg.sender_name,
        message: msg.message,
        messageType: msg.message_type,
        isAdmin: msg.is_admin,
        isRead: msg.is_read,
        createdAt: msg.created_at
      }));
    } catch (error) {
      console.error('Error getting group messages:', error);
      return [];
    }
  }

  /**
   * Send a message to a group
   */
  async sendMessage(
    groupId: string,
    senderId: string,
    senderName: string,
    isAdmin: boolean,
    message: string
  ): Promise<ChatMessage | null> {
    try {
      const { data: newMessage, error } = await supabase
        .from('group_chat_messages')
        .insert({
          group_id: groupId,
          sender_id: senderId,
          sender_name: senderName,
          message: message,
          message_type: 'text',
          is_admin: isAdmin,
          is_read: false
        })
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to send message: ${error.message}`);
      }

      return {
        id: newMessage.id,
        groupId: newMessage.group_id,
        senderId: newMessage.sender_id,
        senderName: newMessage.sender_name,
        message: newMessage.message,
        messageType: newMessage.message_type,
        isAdmin: newMessage.is_admin,
        isRead: newMessage.is_read,
        createdAt: newMessage.created_at
      };
    } catch (error) {
      console.error('Error sending message:', error);
      return null;
    }
  }

  /**
   * Mark messages as read
   */
  async markMessagesAsRead(groupId: string, senderId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('group_chat_messages')
        .update({ is_read: true })
        .eq('group_id', groupId)
        .neq('sender_id', senderId); // Don't mark own messages as read

      if (error) {
        console.error('Error marking messages as read:', error);
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }

  /**
   * Get unread message count for a group
   */
  async getUnreadCount(groupId: string, currentUserId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('group_chat_messages')
        .select('*', { count: 'exact', head: true })
        .eq('group_id', groupId)
        .eq('is_read', false)
        .neq('sender_id', currentUserId);

      if (error) {
        console.error('Error getting unread count:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }
}

export const chatService = new ChatService();
